/**
 * ShareDialog — share one file (ADR-0013 v2): Org / Specific people / Public.
 *
 * Org auto-creates on open (one-click, faster than pasting into external
 * hosts). People and Public require an explicit create — a public link is a
 * deliberate act, never a side effect of opening a dialog. Each scope holds
 * at most one live link (backend dedupes); revoke kills it instantly.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  createShare,
  listDirectory,
  listShares,
  revokeShare,
  type DirectoryUser,
  type ShareScope,
  type ShareTarget,
} from "@/api/shares";
import { parseUTC } from "@/api/types";
import { useToastStore } from "@/components/common/Toast";

interface ShareDialogProps {
  filename: string;
  target: ShareTarget;
  onClose: () => void;
}

const SCOPE_TABS: { key: ShareScope; label: string }[] = [
  { key: "org", label: "Org" },
  { key: "people", label: "Specific people" },
  { key: "public", label: "Public" },
];

const EXPIRY_CHOICES = [7, 30, 90];

/** The slice of a share the dialog needs per scope. */
interface ScopeLink {
  token: string;
  url: string;
  expires_at: string | null;
  allowed_user_ids: string[] | null;
}

function LinkRow({ url, onRevoke, busy }: { url: string; onRevoke: () => void; busy: boolean }) {
  const [copied, setCopied] = useState(false);
  const addToast = useToastStore((s) => s.add);
  const full = window.location.origin + url;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      addToast("Couldn't access the clipboard — copy the link manually.", "error");
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        readOnly
        value={full}
        onFocus={(e) => e.currentTarget.select()}
        className="flex-1 min-w-0 text-xs text-space-50 bg-space-10 border border-space-30
                   rounded-lg px-3 py-2 font-mono truncate"
      />
      <button
        onClick={copy}
        className="shrink-0 py-2 px-3.5 rounded-lg bg-primary text-on-primary text-xs font-semibold
                   hover:opacity-90 transition-opacity"
      >
        {copied ? "Copied ✓" : "Copy link"}
      </button>
      <button
        onClick={onRevoke}
        disabled={busy}
        className="shrink-0 py-2 px-2.5 rounded-lg text-xs font-semibold text-hydra
                   hover:bg-hydra-50/30 transition-colors disabled:opacity-50"
        title="Revoke this link for everyone"
      >
        Revoke
      </button>
    </div>
  );
}

export function ShareDialog({ filename, target, onClose }: ShareDialogProps) {
  const [scope, setScope] = useState<ShareScope>("org");
  // One live share per scope for this target (backend-deduped).
  const [byScope, setByScope] = useState<Partial<Record<ShareScope, ScopeLink>>>({});
  const [directory, setDirectory] = useState<DirectoryUser[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [expiryDays, setExpiryDays] = useState(30);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const addToast = useToastStore((s) => s.add);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [existing, dir] = await Promise.all([
          listShares(target),
          listDirectory().catch(() => ({ users: [] as DirectoryUser[] })),
        ]);
        if (cancelled) return;
        const map: Partial<Record<ShareScope, ScopeLink>> = {};
        for (const s of existing.shares) {
          if (s.mine) {
            map[s.scope] = {
              token: s.token, url: s.url,
              expires_at: s.expires_at, allowed_user_ids: s.allowed_user_ids,
            };
          }
        }
        setDirectory(dir.users);
        // Org is the one-click default: create it up front unless it exists.
        if (!map.org) {
          const created = await createShare(target);
          if (cancelled) return;
          map.org = {
            token: created.token, url: created.url,
            expires_at: created.expires_at, allowed_user_ids: [],
          };
        }
        setByScope(map);
        setLoaded(true);
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message || "Sharing is unavailable.");
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Mounted once per share action with a fixed target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (s: ShareScope) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createShare(target, {
        scope: s,
        allowedUserIds: s === "people" ? [...picked] : undefined,
        expiresInDays: s === "public" ? expiryDays : undefined,
      });
      setByScope((prev) => ({
        ...prev,
        [s]: {
          token: created.token, url: created.url,
          expires_at: created.expires_at,
          allowed_user_ids: created.allowed_user_ids,
        },
      }));
    } catch (e) {
      setError((e as Error).message || "Couldn't create the link.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (s: ShareScope) => {
    const share = byScope[s];
    if (!share || busy) return;
    setBusy(true);
    try {
      await revokeShare(share.token);
      setByScope((prev) => {
        const next = { ...prev };
        delete next[s];
        return next;
      });
      addToast("Share link revoked.", "success");
    } catch (e) {
      addToast((e as Error).message || "Couldn't revoke the link.", "error");
    } finally {
      setBusy(false);
    }
  };

  const togglePicked = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const nameOf = (id: string) =>
    directory.find((u) => u.id === id)?.name || directory.find((u) => u.id === id)?.email || id;

  const active = byScope[scope];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-space/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-space mb-3 truncate">Share “{filename}”</h3>

        <div className="flex items-center gap-1.5 mb-4">
          {SCOPE_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setScope(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                scope === t.key
                  ? "bg-primary text-on-primary"
                  : "text-space-50 hover:text-space hover:bg-space-20"
              }`}
            >
              {t.label}
              {byScope[t.key] && <span className="ml-1">•</span>}
            </button>
          ))}
        </div>

        {error && (
          <p className="text-sm text-hydra bg-hydra-50 rounded-lg px-3 py-2 mb-4">{error}</p>
        )}

        {!loaded && !error && (
          <p className="text-sm text-space-40 mb-4">Loading…</p>
        )}

        {loaded && scope === "org" && (
          <div className="space-y-3 mb-4">
            <p className="text-sm text-space-50 leading-relaxed">
              Anyone at Nbyula who is signed in to OmniWerk can open this link. Views are audited.
            </p>
            {active && <LinkRow url={active.url} onRevoke={() => revoke("org")} busy={busy} />}
          </div>
        )}

        {loaded && scope === "people" && (
          <div className="space-y-3 mb-4">
            {active ? (
              <>
                <p className="text-sm text-space-50 leading-relaxed">
                  Only these people can open the link:{" "}
                  <span className="text-space font-medium">
                    {(active.allowed_user_ids ?? []).map(nameOf).join(", ")}
                  </span>
                  . To change who has access, revoke and create a new link.
                </p>
                <LinkRow url={active.url} onRevoke={() => revoke("people")} busy={busy} />
              </>
            ) : (
              <>
                <p className="text-sm text-space-50 leading-relaxed">
                  Pick who can open the link. Everyone else — including other signed-in
                  teammates — gets nothing.
                </p>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-space-30 divide-y divide-space-30/50">
                  {directory.length === 0 && (
                    <p className="text-xs text-space-40 px-3 py-2.5">No other members found.</p>
                  )}
                  {directory.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-space cursor-pointer hover:bg-jove-10/40"
                    >
                      <input
                        type="checkbox"
                        checked={picked.has(u.id)}
                        onChange={() => togglePicked(u.id)}
                        className="accent-jove"
                      />
                      <span className="truncate">{u.name || u.email}</span>
                      <span className="text-[10px] text-space-40 truncate ml-auto">{u.email}</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => create("people")}
                  disabled={picked.size === 0 || busy}
                  className="w-full py-2 rounded-lg bg-primary text-on-primary text-xs font-semibold
                             hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  Create link for {picked.size || "…"} {picked.size === 1 ? "person" : "people"}
                </button>
              </>
            )}
          </div>
        )}

        {loaded && scope === "public" && (
          <div className="space-y-3 mb-4">
            {active ? (
              <>
                <p className="text-sm text-space-50 leading-relaxed">
                  <span className="font-semibold text-space">Anyone with this link can view</span>
                  {" "}— no sign-in needed.
                  {active.expires_at &&
                    ` Expires ${parseUTC(active.expires_at).toLocaleDateString([], { month: "short", day: "numeric" })}.`}
                </p>
                <LinkRow url={active.url} onRevoke={() => revoke("public")} busy={busy} />
              </>
            ) : (
              <>
                <p className="text-sm text-space-50 leading-relaxed">
                  Creates a link <span className="font-semibold text-space">anyone can open
                  without signing in</span> — for leads and external partners. It expires
                  automatically and admins can see and revoke it at any time.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-space-50">Expires in</span>
                  {EXPIRY_CHOICES.map((d) => (
                    <button
                      key={d}
                      onClick={() => setExpiryDays(d)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                        expiryDays === d
                          ? "bg-primary text-on-primary"
                          : "text-space-50 hover:bg-space-20"
                      }`}
                    >
                      {d} days
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => create("public")}
                  disabled={busy}
                  className="w-full py-2 rounded-lg bg-primary text-on-primary text-xs font-semibold
                             hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  Create public link
                </button>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="py-2 px-4 rounded-lg text-xs font-semibold text-space-50 hover:bg-space-20 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
